/**
 * Printer configuration state for the selected outlet: printers, section and
 * takeaway mappings, and the save actions for each. Shared by the Settings →
 * Printer Settings pages.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useDepartments } from '@hooks/menu';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { usePowerSyncChange } from '@/renderer/lib/powersync/ipcBridge';
import { selectCanManageSectionsTables } from '@store/auth/authSelectors';
import { selectEffectiveOutletId } from '@store/outlets';
import {
  clearPrinterState,
  fetchOutletPrintersThunk,
  fetchPrinterDeviceIdThunk,
  fetchSectionPrinterMappingsThunk,
  fetchTakeAwayPrinterMappingsThunk,
  saveSectionPrinterConfigThunk,
  saveTakeAwayPrinterConfigThunk,
  selectPrinterDeviceId,
  selectPrinterOptions,
  selectPrintersSaving,
  selectSectionPrinterMappings,
  selectTakeAwayPrinterMappings,
} from '@store/printers';
import {
  SaveSectionPrinterConfigRequest,
  SaveTakeAwayPrinterConfigRequest,
  SectionPrinterMappingView,
} from '../../types/printer';
import { apiError, apiSuccess } from '../../util/notify';

const toDepartmentPrinterMap = (
  mappings: { department_id: string | null; printer_id: string }[],
) =>
  mappings.reduce<Record<string, string>>((acc, mapping) => {
    acc[mapping.department_id as string] = mapping.printer_id;
    return acc;
  }, {});

export const usePrinterConfiguration = () => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectEffectiveOutletId);
  const printerOptions = useAppSelector(selectPrinterOptions);
  const sectionMappings = useAppSelector(selectSectionPrinterMappings);
  const takeAwayMappings = useAppSelector(selectTakeAwayPrinterMappings);
  const printerDeviceId = useAppSelector(selectPrinterDeviceId);
  const isSaving = useAppSelector(selectPrintersSaving);
  const canManage = useAppSelector(selectCanManageSectionsTables);
  const { data: allDepartments = [] } = useDepartments(outletId);
  const activeDepartments = useMemo(
    () => allDepartments.filter((d) => d.status === 'active'),
    [allDepartments],
  );

  const reload = useCallback(() => {
    if (!outletId) {
      dispatch(clearPrinterState());
      return;
    }

    dispatch(fetchOutletPrintersThunk(outletId));
    dispatch(fetchSectionPrinterMappingsThunk(outletId));
  }, [dispatch, outletId]);

  useEffect(() => {
    dispatch(fetchPrinterDeviceIdThunk());
  }, [dispatch]);

  useEffect(() => {
    reload();
  }, [reload]);

  usePowerSyncChange(
    [
      'outlet_printers',
      'section_printer_mappings',
      'takeaway_printer_mappings',
    ],
    reload,
  );

  useEffect(() => {
    if (!outletId || !printerDeviceId) return;

    dispatch(
      fetchTakeAwayPrinterMappingsThunk({
        outletId,
        deviceId: printerDeviceId,
      }),
    );
  }, [dispatch, outletId, printerDeviceId]);

  const mappingsForSection = useCallback(
    (sectionId: number): SectionPrinterMappingView[] =>
      sectionMappings.filter((mapping) => mapping.section_id === sectionId),
    [sectionMappings],
  );

  // A section is fully configured when it has a bill printer and a KOT
  // printer for every active department.
  const hasPrinterConfigForSection = useCallback(
    (sectionId: number) => {
      const sectionRows = mappingsForSection(sectionId);
      const billConfigured = sectionRows.some(
        (mapping) => mapping.operation === 'BILL' && !mapping.department_id,
      );
      const everyDepartmentConfigured = activeDepartments.every((department) =>
        sectionRows.some(
          (mapping) =>
            mapping.operation === 'KOT' &&
            mapping.department_id === department.id &&
            Boolean(mapping.printer_id),
        ),
      );

      return billConfigured && everyDepartmentConfigured;
    },
    [activeDepartments, mappingsForSection],
  );

  const getSectionBillPrinterId = useCallback(
    (sectionId: number) =>
      mappingsForSection(sectionId).find(
        (mapping) => mapping.operation === 'BILL' && !mapping.department_id,
      )?.printer_id || null,
    [mappingsForSection],
  );

  const getSectionDepartmentPrinterMap = useCallback(
    (sectionId: number) =>
      toDepartmentPrinterMap(
        mappingsForSection(sectionId).filter(
          (mapping) =>
            mapping.operation === 'KOT' && Boolean(mapping.department_id),
        ),
      ),
    [mappingsForSection],
  );

  const takeAwayBillPrinterId = useMemo(() => {
    if (!printerDeviceId) return null;

    return (
      takeAwayMappings.find(
        (mapping) =>
          mapping.device_id === printerDeviceId &&
          mapping.operation === 'BILL' &&
          !mapping.department_id,
      )?.printer_id || null
    );
  }, [printerDeviceId, takeAwayMappings]);

  const takeAwayDepartmentPrinterMap = useMemo(() => {
    if (!printerDeviceId) return {};

    return toDepartmentPrinterMap(
      takeAwayMappings.filter(
        (mapping) =>
          mapping.device_id === printerDeviceId &&
          mapping.operation === 'KOT' &&
          Boolean(mapping.department_id),
      ),
    );
  }, [printerDeviceId, takeAwayMappings]);

  const saveSectionConfig = useCallback(
    async (payload: SaveSectionPrinterConfigRequest) => {
      if (!outletId || !canManage) {
        apiError('Select an outlet first');
        return false;
      }

      const result = await dispatch(saveSectionPrinterConfigThunk(payload));
      if (saveSectionPrinterConfigThunk.rejected.match(result)) {
        apiError(result.payload || 'Failed to save section printer setup');
        return false;
      }

      apiSuccess('Section printer setup saved');
      return true;
    },
    [canManage, dispatch, outletId],
  );

  const saveTakeAwayConfig = useCallback(
    async (payload: SaveTakeAwayPrinterConfigRequest) => {
      if (!outletId || !canManage) {
        apiError('Select an outlet first');
        return;
      }

      const result = await dispatch(saveTakeAwayPrinterConfigThunk(payload));
      if (saveTakeAwayPrinterConfigThunk.rejected.match(result)) {
        apiError(result.payload || 'Failed to save takeaway printer setup');
        return;
      }

      apiSuccess('Takeaway printer setup saved');
    },
    [canManage, dispatch, outletId],
  );

  const printerSelectOptions = useMemo(
    () =>
      printerOptions.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [printerOptions],
  );

  const departmentOptions = useMemo(
    () =>
      activeDepartments.map((department) => ({
        id: department.id,
        name: department.name,
      })),
    [activeDepartments],
  );

  return {
    outletId,
    canManage,
    isSaving,
    printerDeviceId,
    printerSelectOptions,
    departmentOptions,
    mappingsForSection,
    hasPrinterConfigForSection,
    getSectionBillPrinterId,
    getSectionDepartmentPrinterMap,
    takeAwayBillPrinterId,
    takeAwayDepartmentPrinterMap,
    saveSectionConfig,
    saveTakeAwayConfig,
  };
};
